import { useEffect, useState } from "react";
import { Card, CardContent, Typography, Button, Grid, Input, Divider, Alert, Stack, Box} from "@mui/material";
import AutocompleteComponent from "../Shared/AutocompleteComponent";
import RadiogroupComponent from "../Shared/RadiogroupComponent";
import CountUpComponent from "../Shared/CountUpComponent";
import { ReferencesTableType, ReferencesFilterType, ReferencesMultiFiltersType } from "../../types/dataTypes";
import dayjs from 'dayjs';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FreeTextComponent from "../Shared/FreeTextComonent";
import { postData } from "../../services/base.service";
import { initialFiltersReferences } from "../../utils/consts";
import CustomDrawer from "../Shared/CustomDrawer";

import {ArrowForward, Info, Tune} from "@mui/icons-material";
import CountryRegionAutoComplete from "../Shared/CountryRegionAutoComplete";


type ReferenceFiltersProps = {
  data: ReferencesTableType[];
  setFilteredData: Function;
  setLoading: Function;
  setReload: Function;
  filters: ReferencesFilterType;
  setFilters: Function;
  isDrawerOpen: boolean;
  setIsDrawerOpen: Function;
}

function ReferenceFilters({ data, setFilteredData, setLoading, setReload, filters, setFilters, isDrawerOpen, setIsDrawerOpen}: ReferenceFiltersProps) {
  const [numberOfReferences, setNumberOfReferences] = useState(data.length);
  const [countrySelectectionisAnd, setCountrySelectionisAnd] = useState(true);
  const [fosSelectectionisAnd, setfosSelectectionisAnd] = useState(true);
  const [refNoTitleSelectectionisAnd, setrefNoTitleSelectectionisAnd] = useState(true);
  const [fullTextSearchValue, setFullTextSearchValue] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Add this line for error handling
  const [error, setError] = useState<string | null>(null);

  // State for available options for each filter
  const [availableOptions, setAvailableOptions] = useState<ReferencesMultiFiltersType>({
    countries: [],
    fieldsOfService: [],
    refNumTitles: [],
  });

  const resetFilters = () => {
    setFilters(initialFiltersReferences);
    setFullTextSearchValue("");
    setError(null); // Clear errors when resetting
    setReload(true);
  };

  // Calculate available options based on filters and load related data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null); // Clear previous errors

        //check if full-text filter is applied
        if (fullTextSearchValue !== "") {
          const fullTextResult = await fetchFullTextData();
          
          // Check if the full-text search returned an error
          if (fullTextResult && fullTextResult.success === false) {
            setError(fullTextResult.error || 'An error occurred during full-text search');
            setFilteredData([]);
            setNumberOfReferences(0);
            return;
          }
          
          filters.fullTextProjectNumberList = fullTextResult;
        }

        // calcMinMaxValues();
        const filteredData = filterData('dataset');

        setNumberOfReferences(filteredData.length);

        const newAvailableOptions: ReferencesMultiFiltersType = {
          countries: countrySelectectionisAnd ? Array.from(new Set(filteredData.map(item => item.Country.split(', ')).flat())).sort()
            : Array.from(new Set(filterData('options').map(item => item.Country.split(', ')).flat())).sort(),
          fieldsOfService: fosSelectectionisAnd ? Array.from(new Set(filteredData.map(item => item.FieldOfService.split(/;\s|,\s/)).flat())).sort()
            : Array.from(new Set(filterData('options').map(item => item.FieldOfService.split(/;\s|,\s/)).flat())).sort(),
          refNumTitles: refNoTitleSelectectionisAnd ? Array.from(new Set(filteredData.map(item => item.ProjNumTitle))).sort()
            : Array.from(new Set(filterData('options').map(item => item.ProjNumTitle))).sort(),
        };

        setAvailableOptions(newAvailableOptions);
        setFilteredData(filteredData);
      } catch (error: any) {
        console.error(error);
        setError(error.message || 'An unexpected error occurred');
        setFilteredData([]);
        setNumberOfReferences(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // `data` must be a dependency here: this effect computes counts/options by filtering the
    // `data` prop, and `data` arrives asynchronously from the parent after this component has
    // already mounted (its initial value is `[]`). Without `data` in the deps, this effect only
    // ever ran once against that empty initial array and never recomputed once real data loaded -
    // previously masked by the parent gating its whole render behind `data.length > 0`, which is
    // no longer the case now that loading skeletons replaced that gate.
  }, [filters, countrySelectectionisAnd, fosSelectectionisAnd, refNoTitleSelectectionisAnd, data]);

  const fetchFullTextData = async () => {
    try {
      const fullTextProjectIDList = await postData('references/fulltextsearch', { fulltext: fullTextSearchValue });
      
      // Check if backend returned an error response
      if (fullTextProjectIDList && fullTextProjectIDList.success === false) {
        return fullTextProjectIDList; // Return the error response to be handled above
      }
      
      return fullTextProjectIDList;
    } catch (error: any) {
      console.log(error);
      throw error; // Re-throw to be caught by the main try-catch
    }
  }

  //filter Items function give back a boolean value
  const filterData = (mode: 'dataset' | 'options'): ReferencesTableType[] => {
    const filteredData = data.filter(item => {
      const itemCountries = item.Country.split(', ').flat();
      const itemCountriesUnique = Array.from(new Set(itemCountries));
      const itemFieldsOfServices = item.FieldOfService?.split(/;\s|,\s/).flat();
      const itemFieldsOfServicesUnique = Array.from(new Set(itemFieldsOfServices));

      const fulltextCondition =
        fullTextSearchValue === "" ? true :
          filters.fullTextProjectNumberList.length === 0 ? false : filters.fullTextProjectNumberList.includes(item.ProjectNo);

      const countryCondition = filters.countries.length === 0 ||
        (mode === 'dataset' ? countrySelectectionisAnd ?
          filters.countries.some(country => itemCountriesUnique.includes(country)) :
          filters.countries.some(country => itemCountriesUnique.includes(country)) : true);

      const fosCondition = filters.fieldsOfServices.length === 0 ||
        (mode === 'dataset' ? fosSelectectionisAnd ?
          filters.fieldsOfServices.some(service => itemFieldsOfServicesUnique.includes(service)) :
          filters.fieldsOfServices.some(service => itemFieldsOfServicesUnique.includes(service)) : true);

      const refNumTitlesCondition = filters.refNumTitles.length === 0 ||
        (mode === 'dataset' ? (refNoTitleSelectectionisAnd ?
          filters.refNumTitles.some(title => item.ProjNumTitle.includes(title)) :
          filters.refNumTitles.some(title => item.ProjNumTitle.includes(title))) : true);

      const filteredPrintSelection = filters.printSelectionList.length > 0 ?
        filters.printSelectionList.includes(item.ProjectNo) : true;

      return dayjs(item.CompletionDate) >= filters.completionDate &&
        item.ProjectValueEuro >= filters.projectValueEuro &&
        countryCondition &&
        fosCondition &&
        (filters.selectedCompany === 'both' ? item.INTEE === true || item.INTEE === false :
          filters.selectedCompany === 'intee' ? item.INTEE === true :
            filters.selectedCompany === 'intFra' ? item.INTEE === false : false) &&
        refNumTitlesCondition && fulltextCondition && filteredPrintSelection;
    })
    return filteredData;
  };

  // Handler functions to update filters
  const handleYearChange = (newValue: any) => {
    setFilters((prevFilters: any) => ({ ...prevFilters, completionDate: newValue }));
  };

  const handleProjectValueEuroChange = (newValue: number) => {
    setFilters((prevFilters: any) => ({ ...prevFilters, projectValueEuro: newValue }));
  };

  const handleFieldOfServiceChange = (event: any, newValue: any) => {
    setFilters((prevFilters: any) => ({ ...prevFilters, fieldsOfServices: newValue }));
  };

  const handleCompanyChange = (newValue: any) => {
    console.log(newValue);
    setFilters((prevFilters: any) => ({ ...prevFilters, selectedCompany: newValue }));
  };

  const handlerefNumTitle = (event: any, newValue: any) => {
    setFilters((prevFilters: any) => ({ ...prevFilters, refNumTitles: newValue }));
  };

  const handleFullTextSearch = (searchValue: any) => {
    setFilters((prevFilters: any) => ({ ...prevFilters, fullText: searchValue }));
  };

  return (
    <>
      {/* Add this error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1} justifyContent="space-between">
      <Grid item xs={12} md={10}>
      <Card sx={{marginTop:0}} >
      <CardContent>
        <Grid container spacing={1} justifyContent="space-between">
        <Grid item xs={12} md={6}>
          <FreeTextComponent
            onSearch={handleFullTextSearch}
            label="Full Text Search"
            inputValue={fullTextSearchValue}
            setInputValue={setFullTextSearchValue}
            setIsDialogOpen={setIsDialogOpen}
            dialogOpen={isDialogOpen}
          />
        <Button sx={{paddingTop:2}} variant="text" color="primary" onClick={() => setIsDrawerOpen((prevState: any) => !prevState)}  
        startIcon={<ArrowForward />}>
          Advanced Filters
        </Button>
        </Grid>
        <Grid item xs={12} md={5}>
          <AutocompleteComponent
            options={availableOptions.refNumTitles}
            value={filters.refNumTitles}
            handleChange={handlerefNumTitle}
            isMulti={true}
            label="Reference Number and Title"
            radioState={refNoTitleSelectectionisAnd}
            setRadioState={setrefNoTitleSelectectionisAnd}
          />
        </Grid>
        </Grid>
        </CardContent>
        </Card>
        </Grid>
        <Grid item xs={12} md={2}>         
        <CountUpComponent resetFilters = {resetFilters} end={numberOfReferences} />
        </Grid>
      </Grid>
      <CustomDrawer open={isDrawerOpen} toggleDrawer={setIsDrawerOpen} vw={20} title={'Advanced Filter'} orientation="left">
        <Stack direction="row" alignItems="center" gap={1} paddingBottom={2}>
          <Info color="action" />
          <Typography variant="h6">General</Typography>
        </Stack>
        <Grid container spacing={1} paddingTop={2}>
          <Grid item xs={10} md={10}>
            <CountryRegionAutoComplete
              availableOptions={availableOptions}
              filters={filters}
              setFilters={setFilters}
              countrySelectectionisAnd={countrySelectectionisAnd}
              setCountrySelectionisAnd={setCountrySelectionisAnd}
              />
          </Grid>
          <Grid item xs={10} md={10}>
            <AutocompleteComponent
              options={availableOptions.fieldsOfService}
              value={filters.fieldsOfServices}
              handleChange={handleFieldOfServiceChange}
              isMulti={true}
              label="Field of Service"
              radioState={fosSelectectionisAnd}
              setRadioState={setfosSelectectionisAnd}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            marginTop: 4,
            marginBottom: 2,
            borderRadius: 1,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Tune color="action" />
            <Typography variant="h6">
              Reference Criteria
            </Typography>
          </Stack>
        </Box>
        <Grid container spacing={1} marginTop={1}>
          <Grid item xs={12} md={12}>
            <Typography sx={{ fontWeight: 'bold', paddingBottom: 1 }}>Completion Date After</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={filters.completionDate}
                onChange={(newValue: any) => handleYearChange(newValue)}
                slotProps={{
                  textField: {
                    disabled: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={12}>
            <Divider style={{ color: 'lightgrey', paddingTop: 20, paddingBottom: 20 }} />
          </Grid>
          <Grid item xs={12} md={12}>
            <Typography sx={{ fontWeight: 'bold', paddingBottom: 1 }}>Project Value € Above </Typography>
            <Input
              value={filters.projectValueEuro}
              onChange={(event) => handleProjectValueEuroChange(Number(event.target.value))}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Divider style={{ color: 'lightgrey', paddingTop: 20, paddingBottom: 20 }} />
          </Grid>
          <Grid item xs={12} md={12}>
            <Typography sx={{ fontWeight: 'bold', paddingBottom: 1 }}>Company Selection</Typography>
            <RadiogroupComponent
              value={filters.selectedCompany}
              handleChange={handleCompanyChange}
            />
          </Grid>
        </Grid>
      </CustomDrawer>
    </>
  );
}

export default ReferenceFilters;